import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { ArrowUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import { toast } from 'react-hot-toast';
import { useAuthStore } from '../stores/authStore';

interface Operator {
  id: string;
  firstName: string;
  lastName: string;
  username: string;
  department?: string;
}

interface EscalationPanelProps {
  ticketId: string;
  currentSupportLine: number;
  onEscalated: () => void;
  // Compact mode (Workspace): render just a small "↑ 2-я линия" button that
  // sits inline among the other action buttons instead of a full-width bar in
  // its own boxed card. The expand form drops below as a full-width row.
  compact?: boolean;
}

const EscalationPanel: React.FC<EscalationPanelProps> = ({
  ticketId,
  currentSupportLine,
  onEscalated,
  compact = false,
}) => {
  const { user } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [selectedOperatorId, setSelectedOperatorId] = useState('');
  const [secondLineOperators, setSecondLineOperators] = useState<Operator[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingOperators, setLoadingOperators] = useState(false);

  useEffect(() => {
    if (isOpen && currentSupportLine === 1) {
      fetchSecondLineOperators();
    }
  }, [isOpen, currentSupportLine]);

  const fetchSecondLineOperators = async () => {
    try {
      setLoadingOperators(true);
      const response = await api.get('/api/users');
      
      if (response.data.success) {
        const users = response.data.data?.users || response.data.data || [];
        setSecondLineOperators(users.filter((u: any) => 
          u.role === 'operator' && 
          u.supportLine === 2 && 
          u.canHandleEscalations &&
          u.isActive
        ));
      }
    } catch (error: any) {
      console.error('Error fetching second line operators:', error);
    } finally {
      setLoadingOperators(false);
    }
  };

  const handleEscalate = async () => {
    if (!reason.trim()) {
      toast.error('Укажите причину эскалации');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post(`/api/tickets/${ticketId}/escalate`, {
        reason: reason.trim(),
        toOperatorId: selectedOperatorId || undefined
      });

      if (response.data.success) {
        toast.success('Заявка успешно эскалирована на 2-ю линию');
        setIsOpen(false);
        setReason('');
        setSelectedOperatorId('');
        onEscalated();
      }
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Ошибка при эскалации заявки');
    } finally {
      setLoading(false);
    }
  };

  // Не показываем панель, если заявка уже на 2-й линии или пользователь не может эскалировать
  if (currentSupportLine === 2) {
    return null;
  }

  if (user?.role !== 'operator' && user?.role !== 'admin' && user?.role !== 'superadmin') {
    return null;
  }

  const triggerButton = (
    <Button
      variant="outline"
      size={compact ? 'sm' : undefined}
      onClick={() => setIsOpen(true)}
      className={compact ? '' : 'w-full'}
      title={compact ? 'Эскалировать на 2-ю линию' : undefined}
    >
      <ArrowUpIcon className={`h-4 w-4 ${compact ? 'mr-1' : 'mr-2'}`} />
      {compact ? '2-я линия' : 'Эскалировать на 2-ю линию'}
    </Button>
  );

  const form = (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-medium text-foreground">
              Эскалация на 2-ю линию поддержки
            </h3>
            <Button
              variant="ghost"
              size="icon-sm"
              onClick={() => {
                setIsOpen(false);
                setReason('');
                setSelectedOperatorId('');
              }}
            >
              <XMarkIcon className="h-4 w-4" />
            </Button>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Причина эскалации *
            </label>
            <Textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Опишите причину, по которой заявка требует эскалации на 2-ю линию..."
              rows={3}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Назначить конкретному оператору (необязательно)
            </label>
            {loadingOperators ? (
              <div className="text-sm text-muted-foreground">Загрузка операторов...</div>
            ) : (
              <select
                value={selectedOperatorId}
                onChange={(e) => setSelectedOperatorId(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground"
              >
                <option value="">Оставить в очереди 2-й линии</option>
                {secondLineOperators.map(operator => (
                  <option key={operator.id} value={operator.id}>
                    {operator.firstName} {operator.lastName} (@{operator.username})
                    {operator.department && ` - ${operator.department}`}
                  </option>
                ))}
              </select>
            )}
          </div>

          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setIsOpen(false);
                setReason('');
                setSelectedOperatorId('');
              }}
              disabled={loading}
            >
              Отмена
            </Button>
            <Button
              onClick={handleEscalate}
              disabled={loading || !reason.trim()}
            >
              {loading ? 'Эскалация...' : 'Эскалировать'}
            </Button>
          </div>
        </div>
  );

  // Compact: just the inline button; when open, the form drops to its own
  // full-width row (parent is a flex-wrap action bar).
  if (compact) {
    return isOpen ? (
      <div className="w-full mt-2 border border-border rounded-lg p-3 bg-card">{form}</div>
    ) : (
      triggerButton
    );
  }

  return (
    <div className="border border-border rounded-lg p-4 bg-card">
      {!isOpen ? triggerButton : form}
    </div>
  );
};

export default EscalationPanel;

